import React, { useEffect, useMemo } from 'react';
import { Grid } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import Lottie from 'react-lottie-player';
import { Meta } from 'Interfaces';
import { animateScroll } from 'react-scroll';
import Newsletter from '../../sections/newsletter/NewsletterComponent';
import Form from '../../sections/free-trail/form';
import powerBiPageStyles from './PowerBiPage.styles';
import useSetMeta from '../../../hooks/useSetMeta';

import powerBiItVisualization from '../../../assets/icons/powerbi/visualization_pbi.svg';
import powerBiAccess from '../../../assets/icons/powerbi/accessAnywere_pbi.svg';
import powerBiMultipleData from '../../../assets/icons/powerbi/multipleData_pbi.svg';
import powerBiEasyUse from '../../../assets/icons/powerbi/easyUser_pbi.svg';
import powerBiSecurity from '../../../assets/icons/powerbi/security_pbi.svg';

import powerBiCheck from '../../../assets/icons/powerbi/benefits_check.svg';
import powerBiPC from '../../../assets/icons/powerbi/benefitsPc_pbi.svg';

import idokHelpPBI1 from '../../../assets/icons/powerbi/idokHelp_pbi_1.svg';
import idokHelpPBI2 from '../../../assets/icons/powerbi/idokHelp_pbi_2.svg';

import bannerAnimation from '../../../assets/animations/animacao-idok-powerbi.json';

const tools = [
  {
    icon: powerBiItVisualization,
    key: 'powerBi.tools.visualization',
  },
  {
    icon: powerBiAccess,
    key: 'powerBi.tools.access',
  },
  {
    icon: powerBiMultipleData,
    key: 'powerBi.tools.connection',
  },
  {
    icon: powerBiEasyUse,
    key: 'powerBi.tools.easyUse',
  },
  {
    icon: powerBiSecurity,
    key: 'powerBi.tools.security',
  }
]

const benefits = [
  {
    key: 'powerBi.benefits.one',
  },
  {
    key: 'powerBi.benefits.two',
  },
  {
    key: 'powerBi.benefits.three',
  },
  {
    key: 'powerBi.benefits.four',
  },
  {
    key: 'powerBi.benefits.five',
  }
];

const idokHelpPowerBi = [
  {
    icon: idokHelpPBI1,
    key: 'powerBi.helpContainer.helpOne',
  },
  {
    icon: idokHelpPBI2,
    key: 'powerBi.helpContainer.helpTwo',
  },
];

function PowerBiPage() {
  const classes = powerBiPageStyles();
  const { t: translation } = useTranslation();

  const metaTags: Array<Meta> = useMemo(() => ([
    { name: 'title', content: translation('powerBi.meta.title') },
    { name: 'description', content: translation('powerBi.meta.description') }
  ]), [translation]);
  useSetMeta(metaTags);

  useEffect(() => {
    document.title = translation('powerBi.meta.title')
  }, []);

  useEffect(() => {
    animateScroll.scrollToTop();
  }, []);

  return (
    <Grid
      container
      direction='column'
      alignItems='stretch'
      className={classes.root}
    >
      <Grid item className={classes.introductionContainer}>
        <div className={classes.introductionInfoContainer}>
          <h3 className={classes.introductionIntegration}>{translation('integracaoIdok')}</h3>
          <h1>
            <span>{translation('powerBi.introductionContainer.mainTitleOne')} </span>
            <span>{translation('powerBi.introductionContainer.mainTitleTwo')}</span>
            <span>{translation('powerBi.introductionContainer.mainTitleThree')}</span>
          </h1>
          <div className={classes.separator}></div>
          <div className={classes.introductionImgContainerSmall}>
            <Lottie
              loop={false}
              animationData={bannerAnimation}
              play={true}
              renderer="svg"
              className="banner-image"
              title={translation('powerBi.introductionContainer.question')}
              style={{
                width: '100%',
                height: '100%'
              }}
            />
          </div>
          <h3 className={classes.whatIsPowerBi}>{translation('powerBi.introductionContainer.question')}</h3>
          <p className={classes.whatIsStampsAnswer}>{translation('powerBi.introductionContainer.questionAnswer')}</p>
        </div>
        <div className={classes.introductionImgContainer}>
          <Lottie
            loop={false}
            animationData={bannerAnimation}
            play={true}
            renderer="svg"
            className="banner-image"
            title={translation('powerBi.introductionContainer.question')}
            style={{
              width: '100%',
              height: '100%'
            }}
          />
        </div>
      </Grid>

      <Grid item className={classes.idokPBiContainer}>
        <div className={classes.idokPBiTextContainer}>
          <h2 className={classes.idokPBiTitle}>
            {translation('powerBi.idokPbiContainer.left.mainTitleOne')}
            <span> {translation('powerBi.idokPbiContainer.left.mainTitleTwo')}</span>
          </h2>
          <p>{translation('powerBi.idokPbiContainer.left.mainParagraph')}</p>
          <p>{translation('powerBi.idokPbiContainer.left.secondParagraph')}</p>
          <p>{translation('powerBi.idokPbiContainer.left.thirdParagraph')}</p>
        </div>
        <div className={classes.idokPBiTextContainer}>
          <h2 className={classes.idokPBiTitle}>
            <span> {translation('powerBi.idokPbiContainer.right.mainTitlePreOne')}</span>
            {translation('powerBi.idokPbiContainer.right.mainTitleOne')}
            <span> {translation('powerBi.idokPbiContainer.right.mainTitleTwo')}</span>
          </h2>
          <p>{translation('powerBi.idokPbiContainer.right.mainParagraph')}</p>
          <p>{translation('powerBi.idokPbiContainer.right.secondParagraph')}</p>
          <p>{translation('powerBi.idokPbiContainer.right.thirdParagraph')}</p>
        </div>
      </Grid>

      <Grid item className={classes.advantagesContainer}>
        <div className={classes.advantagesParagraphContainer}>
          <h2>
            <span className={classes.advantagesParagraphText}>
              <span className={classes.paragraphYellowBox}></span>
              {translation('powerBi.advantagesContainer.mainTitleOne')}
              <span className={classes.paragraphYellowText}> {translation('powerBi.advantagesContainer.mainTitleTwo')} </span>
            </span>
          </h2>
        </div>
        <div className={classes.toolCardsContainer}>
          {tools.map((tool, index) => {
            const textoBold = translation(tool.key + 'Bold');
            const texto     = translation(tool.key + 'Text');
            const altTitle  = translation(tool.key + 'Alt');
            return (
              <div key={index} className={classes.toolCardContent}>
                <div className={classes.toolCardContentImgContainer}>
                  <img src={tool.icon} alt={altTitle} title={altTitle} />
                </div>
                <div className={classes.toolCardContentTextContainer}>
                  <p className='bold'>{textoBold}</p>
                  <p>{texto}</p>
                </div>
              </div>
            )
          })}
        </div>
      </Grid>

      <Grid item className={classes.securityContainer}>
        <div className={classes.infoSecurityContainer}>
          <div className={classes.securityTextContainer}>
            <h2>
              {translation('powerBi.helpContainer.title')}
            </h2>
            <p>{translation('powerBi.helpContainer.mainParagraphOne')}</p>
            <div className={classes.idokHelpCardsContainer}>
              {idokHelpPowerBi.map((help, index) => {
                return (
                  <div key={index} className='card'>
                    <img src={help.icon} alt={translation('powerBi.helpContainer.helpAlt')} title={translation('powerBi.helpContainer.helpAlt')} />
                    <span>{translation(help.key)}</span>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
        <div className={classes.securityBackgroundBoxOne}></div>
        <div className={classes.securityBackgroundBoxTwo}></div>
        <div className={classes.securityBackgroundBoxThree}></div>
      </Grid>

      <Grid item className={classes.gtsIntegrationContainer}>
        <div className={classes.gtsIntegrationTextContainer}>
          <h3 className={classes.gtsIntegrationTextTitle}>
            {translation('powerBi.benefitsContainer.mainTitleOne')}
            <span> {translation('powerBi.benefitsContainer.mainTitleTwo')}</span>
          </h3>

          <div className={classes.useBenefits}>
            {benefits.map((benefit, index) => {
              return (
                <div key={index} className={classes.benefitContainer}>
                  <img src={powerBiCheck} alt={translation('powerBi.benefits.checkAlt')} title={translation('powerBi.benefits.checkAlt')} />
                  <span>{translation(benefit.key)}</span>
                </div>
              )
            })}
          </div>
        </div>
        <div className={classes.gtsIntegrationImgContainer}>
          <img src={powerBiPC} alt={translation('powerBi.benefitsContainer.imgAlt')} title={translation('powerBi.gtsIntegrationContainer.gtsIntegrationAlt')} />
        </div>
      </Grid>


      <Grid item className={classes.advantagesContainer}>
        <div className={classes.advantagesParagraphContainer}>
          <h2>
            <span className={classes.advantagesParagraphText}>
              <span className={classes.paragraphYellowBox}></span>
              {translation('powerBi.whyPowerBi.mainTitleOne')}
              <span className={classes.paragraphYellowText}> {translation('powerBi.whyPowerBi.mainTitleTwo')}</span>
              {translation('powerBi.whyPowerBi.mainTitleThree')}
              {translation('powerBi.whyPowerBi.mainTitleFour')}
            </span>
          </h2>
        </div>
      </Grid>

      <Grid container className={classes.formContainer}>
        <div className={classes.formTitleContainer}>
          <h3 className={classes.formTitle}>
            {translation('duvidasTitle')}
          </h3>
        </div>
        <Form message="askQuestionPrimavera" supportType="4"/>
      </Grid>
      <Grid item className={classes.newsLetterContainer}>
          <Newsletter boxPosition="" />
      </Grid>
    </Grid>
  )
}

export default PowerBiPage;
